/**
 * CreatorProfilesRepo — typed repository over {@link DocStorePort} for
 * `creatorProfiles/{uid}`. A creator's public profile card (handle, bio,
 * avatar). `gamesCount` is maintained by a function; this repo defaults it to 0
 * on create and never authors it on update.
 */
import { PlatformError, type DocStorePort, type Uid } from '@djgg/core';
import { creatorProfileDoc, creatorProfilesCollection } from '../paths.js';
import type { CreatorProfile, QuotaOverrides } from '../types.js';

/** Fields to create a profile (the rest are defaulted). */
export interface CreateCreatorProfileInput {
  uid: Uid;
  handle: string;
  bio?: string;
  avatarKey?: string;
}

/** Fields the owner may update. */
export type UpdateCreatorProfileInput = Partial<Pick<CreatorProfile, 'handle' | 'bio' | 'avatarKey'>>;

export class CreatorProfilesRepo {
  constructor(private readonly store: DocStorePort) {}

  /** Fetch a profile, or null. */
  async get(uid: Uid): Promise<CreatorProfile | null> {
    return this.store.getDoc<CreatorProfile>(creatorProfileDoc(uid));
  }

  /**
   * Resolve a handle to its profile (+ owner uid), or null. Equality filter on
   * the canonical-lowercase stored form (migration 0022's unique lower(handle)
   * index makes the answer well-defined); callers canonicalize input first.
   */
  async getByHandle(handle: string): Promise<(CreatorProfile & { uid: Uid }) | null> {
    const rows = await this.store.query<CreatorProfile>(creatorProfilesCollection(), {
      where: [['handle', '==', handle]],
      limit: 1,
    });
    const row = rows[0];
    if (!row) return null;
    const { id, ...profile } = row;
    return { ...(profile as CreatorProfile), uid: id };
  }

  /**
   * Create a profile. Throws `PlatformError('conflict')` if one already exists
   * for the uid.
   */
  async create(input: CreateCreatorProfileInput): Promise<CreatorProfile> {
    if (await this.get(input.uid)) {
      throw new PlatformError('conflict', `creator profile ${input.uid} already exists`);
    }
    const write: Record<string, unknown> = {
      handle: input.handle,
      bio: input.bio ?? '',
      avatarKey: input.avatarKey ?? '',
      joinedAt: this.store.serverTimestamp(),
      gamesCount: 0,
    };
    await this.store.setDoc(creatorProfileDoc(input.uid), write);
    return (await this.get(input.uid))!;
  }

  /**
   * Update owner-editable fields. `gamesCount` and `joinedAt` are never authored
   * here (server-owned / immutable). Throws `PlatformError('not-found')` if
   * absent.
   */
  async update(uid: Uid, patch: UpdateCreatorProfileInput): Promise<void> {
    const data: Record<string, unknown> = {};
    if (patch.handle !== undefined) data.handle = patch.handle;
    if (patch.bio !== undefined) data.bio = patch.bio;
    if (patch.avatarKey !== undefined) data.avatarKey = patch.avatarKey;
    if (Object.keys(data).length === 0) return;
    await this.store.update(creatorProfileDoc(uid), data);
  }

  /**
   * Remove a profile outright — the offboarding path (`tools/ops/offboard-user.mjs`)
   * for an account that is being deleted from auth. Nothing in the product
   * deletes a profile; a sign-in recreates the stub, so this is only ever the
   * last step before the auth user itself goes.
   */
  async delete(uid: Uid): Promise<void> {
    await this.store.deleteDoc(creatorProfileDoc(uid));
  }

  /**
   * One-time profile CLAIM (the /welcome step): writes handle + avatarKey +
   * the `claimedAt` stamp in one update. `claimedAt` is server-owned — this is
   * its only author, and the updateMyProfile handler invokes it only while the
   * stamp is unset (claim-once). Same underlying write path as {@link update}
   * (`store.update` on the profile doc). Throws `PlatformError('not-found')`
   * if the profile is absent.
   */
  async claimProfile(uid: Uid, input: { handle: string; avatarKey: string }): Promise<void> {
    await this.store.update(creatorProfileDoc(uid), {
      handle: input.handle,
      avatarKey: input.avatarKey,
      claimedAt: this.store.serverTimestamp(),
    });
  }

  /**
   * Superadmin-only: patch a creator's quota overrides (a field set to `null`
   * lifts that cap; omitted fields are left as-is). Reads the current profile,
   * merges the patch onto its `quotaOverrides` (jsonb columns replace whole,
   * so the merge happens here, not at the DB layer), and writes the full
   * merged object back via `update` (the row must already exist). Throws
   * `PlatformError('not-found')` if the profile is absent.
   */
  async setQuotaOverride(uid: Uid, patch: Partial<QuotaOverrides>): Promise<QuotaOverrides> {
    const existing = await this.get(uid);
    if (!existing) throw new PlatformError('not-found', `creator profile ${uid} not found`);
    const next: QuotaOverrides = { ...existing.quotaOverrides, ...patch };
    await this.store.update(creatorProfileDoc(uid), { quotaOverrides: next });
    return next;
  }
}
